import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { AuthTokenUtil } from '@/utils/authToken'

// Dynamic imports for code splitting - each route will be in its own chunk

// Define routes
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/public/LandingPage.vue'),
    meta: {
      title: 'SODA Registry - Home'
    }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/Dashboard.vue'),
    meta: {
      title: 'Admin Dashboard - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/projects',
    name: 'AdminProjects',
    component: () => import('@/views/admin/Projects/Projects.vue'),
    meta: {
      title: 'Projects - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/project/:projectKey',
    name: 'AdminProjectDetail',
    component: () => import('@/views/admin/Projects/ProjectDetail.vue'),
    meta: {
      title: 'Project Details - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/accounts',
    name: 'AdminAccounts',
    component: () => import('@/views/admin/Accounts/Accounts.vue'),
    meta: {
      title: 'Accounts - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/account/:accountId',
    name: 'AdminAccountDetail',
    component: () => import('@/views/admin/Accounts/AccountDetail.vue'),
    meta: {
      title: 'Account Details - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/methodologies',
    name: 'AdminMethodologies',
    component: () => import('@/views/admin/Methodologies/Methodologies.vue'),
    meta: {
      title: 'Methodologies - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/methodology/:id',
    name: 'AdminMethodologyDetail',
    component: () => import('@/views/admin/Methodologies/MethodologyDetail.vue'),
    meta: {
      title: 'Methodology Details - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/transaction/:transactionId',
    name: 'AdminMintDetail',
    component: () => import('@/views/admin/Transactions/MintDetail.vue'),
    meta: {
      title: 'Mint Transaction Details - SODA Registry',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/not-authorized',
    name: 'NotAuthorized',
    component: () => import('@/views/auth/NotAuthorized.vue'),
    meta: {
      title: 'Access Denied - SODA Registry'
    }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/auth/CallbackPage.vue'),
    meta: {
      title: 'Signing In...'
    }
  },
  {
    path: '/auth/logout',
    name: 'AuthLogout',
    component: () => import('@/views/auth/LogoutPage.vue'),
    meta: {
      title: 'Signed Out'
    }
  }
]

// Create router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
  // Scroll behavior for better UX
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Set page title
  if (to.meta?.title) {
    document.title = to.meta.title as string
  }

  // Check if route requires authentication (either explicit meta or admin path)
  const requiresAuth = to.meta?.requiresAuth || to.path.startsWith('/admin')
  const requiresAdmin = to.meta?.requiresAdmin

  if (requiresAuth) {
    const authStore = useAuthStore()

    // Check if user is authenticated
    if (!authStore.isLoggedIn) {
      // Try to restore auth state from storage
      const isAuthenticated = await authStore.checkAuthState()

      if (!isAuthenticated) {
        // Clear any remaining auth state and redirect to home page
        authStore.clearAuthentication()
        next('/')
        return
      }
    }

    // Check if access token is expired and refresh if needed
    if (authStore.isTokenExpired()) {
      const refreshResult = await authStore.refreshTokens()

      if (!refreshResult.success) {
        // Refresh failed, clear auth state and redirect to home page
        authStore.clearAuthentication()
        next('/')
        return
      }
    }

    // Check if route requires admin access
    if (requiresAdmin) {
      const isAdmin = AuthTokenUtil.isTenantAdmin()

      if (!isAdmin) {
        // User is authenticated but not an admin
        next('/not-authorized')
        return
      }
    }
  }

  next()
})

export default router